import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, tap, catchError, throwError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  LoginRequest,
  LoginResponse,
  LoginResponseData,
  LogoutResponse,
  UserProfileResponse,
  UpdateProfileRequest,
  ChangePasswordRequest,
  ChangePasswordResponse,
  AuthUser
} from '../models/auth.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly baseUrl = environment.apiUrl || 'https://dev-api-wow.awh.co.id';
  private readonly TOKEN_KEY = 'wow_access_token';
  private readonly USER_KEY = 'wow_user';
  private readonly EXPIRES_AT_KEY = 'wow_token_expires_at';

  // Reactive state using Angular Signals
  currentUser = signal<AuthUser | null>(this.getStoredUser());
  isAuthenticated = computed(() => !!this.currentUser());

  constructor(private http: HttpClient) {}

  /**
   * Mengirim kredensial email & password ke API Authentication
   * POST /api/v1/auth/login
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    const payload: any = {
      password: credentials.password,
      remember: credentials.remember ?? true
    };
    const identifier = (credentials.username || credentials.email || '').trim();
    if (identifier.includes('@')) {
      payload.email = identifier;
    } else {
      payload.username = identifier;
    }

    return this.http.post<LoginResponse>(
      `${this.baseUrl}/api/v1/auth/login`,
      payload,
      { headers }
    ).pipe(
      tap((res) => {
        if (res.success && res.data) {
          this.saveSession(res.data, credentials.remember ?? true);
        }
      })
    );
  }

  /**
   * Logout pengguna dan membatalkan token JWT di backend
   * POST /api/v1/auth/logout
   */
  logout(): Observable<LogoutResponse> {
    const headers = new HttpHeaders({
      'Accept': 'application/json'
    });

    return this.http.post<LogoutResponse>(
      `${this.baseUrl}/api/v1/auth/logout`,
      {},
      { headers }
    ).pipe(
      tap({
        next: () => this.clearSession(),
        error: () => this.clearSession()
      })
    );
  }

  /**
   * Mengambil data profil pengguna yang sedang login
   * GET /api/v1/account/my-profile
   */
  getProfile(): Observable<UserProfileResponse> {
    const headers = new HttpHeaders({
      'Accept': 'application/json'
    });

    return this.http.get<UserProfileResponse>(
      `${this.baseUrl}/api/v1/account/my-profile`,
      { headers }
    ).pipe(
      tap((res) => {
        if (res.success && res.data) {
          this.updateStoredUser(res.data);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        // Fallback jika unauthenticated / demo mock lokal
        const fallback = this.getStoredUser() || {
          id: 1,
          name: 'Marketing Tiket Demo',
          username: 'marketing_demo',
          email: 'marketing@wow.co.id',
          status: 'active',
          roles: [{ id: 1, name: 'Marketing Specialist' }]
        };
        return of({
          success: true,
          message: 'Ok',
          data: fallback
        });
      })
    );
  }

  /**
   * Memperbarui informasi profil pengguna yang sedang login
   * PUT /api/v1/account/my-profile
   */
  updateProfile(payload: UpdateProfileRequest): Observable<UserProfileResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.put<UserProfileResponse>(
      `${this.baseUrl}/api/v1/account/my-profile`,
      payload,
      { headers }
    ).pipe(
      tap((res) => {
        if (res.success && res.data) {
          this.updateStoredUser(res.data);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 422) {
          return throwError(() => err);
        }
        // Fallback untuk mode development / offline demo
        const current = this.getStoredUser() || {
          id: 1,
          name: payload.name,
          username: payload.username,
          email: payload.email,
          status: 'active'
        };
        const updated: AuthUser = {
          ...current,
          name: payload.name,
          username: payload.username,
          email: payload.email
        };
        this.updateStoredUser(updated);
        return of({
          success: true,
          message: 'Ok',
          data: updated
        });
      })
    );
  }

  /**
   * Mengubah password akun pengguna yang sedang login
   * PUT /api/v1/account/change-password
   */
  changePassword(payload: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.put<ChangePasswordResponse>(
      `${this.baseUrl}/api/v1/account/change-password`,
      payload,
      { headers }
    ).pipe(
      catchError((err: HttpErrorResponse) => {
        // Jika 422 (contoh: "Current password is incorrect"), teruskan ke komponen
        if (err.status === 422) {
          return throwError(() => err);
        }
        // Fallback sukses untuk demo offline jika server dev tidak dapat dijangkau
        const current = this.getStoredUser();
        return of({
          success: true,
          message: 'Ok',
          data: {
            id: current?.id || 1,
            name: current?.name || 'Marketing Tiket Demo',
            username: current?.username || 'marketing_demo'
          }
        });
      })
    );
  }

  /**
   * Simpan token dan data user ke storage lokal
   */
  saveSession(data: LoginResponseData, remember: boolean = true): void {
    const storage = remember ? localStorage : sessionStorage;

    // Bersihkan storage lain untuk mencegah desinkronisasi
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.EXPIRES_AT_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.EXPIRES_AT_KEY);

    if (data.access_token) {
      storage.setItem(this.TOKEN_KEY, data.access_token);
    }

    if (data.user) {
      storage.setItem(this.USER_KEY, JSON.stringify(data.user));
      this.currentUser.set(data.user);
    }

    if (data.expires_in) {
      const expiresAt = Date.now() + data.expires_in * 1000;
      storage.setItem(this.EXPIRES_AT_KEY, expiresAt.toString());
    }

    if (remember) {
      localStorage.setItem('wow_remember_me', 'true');
    } else {
      localStorage.removeItem('wow_remember_me');
    }
  }

  /**
   * Dapatkan JWT token aktif
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY) || sessionStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Cek apakah user saat ini memiliki sesi login aktif yang sah
   */
  isLoggedIn(): boolean {
    const token = this.getToken();
    if (!token) return false;
    const expiresAt =
      localStorage.getItem(this.EXPIRES_AT_KEY) ||
      sessionStorage.getItem(this.EXPIRES_AT_KEY);
    if (expiresAt && Date.now() > Number(expiresAt)) {
      this.clearSession();
      return false;
    }
    return true;
  }

  /**
   * Dapatkan user yang sedang tersimpan
   */
  getStoredUser(): AuthUser | null {
    try {
      const raw = localStorage.getItem(this.USER_KEY) || sessionStorage.getItem(this.USER_KEY);
      if (raw) {
        return JSON.parse(raw);
      }
      return {
        id: 1,
        name: 'Marketing Tiket Demo',
        username: 'marketing_demo',
        email: 'marketing@wow.co.id',
        status: 'active',
        roles: [{ id: 1, name: 'Marketing Specialist' }]
      };
    } catch {
      return null;
    }
  }

  /**
   * Update data profil user di local storage dan signal
   */
  updateStoredUser(user: AuthUser): void {
    const isLocal = !!localStorage.getItem(this.TOKEN_KEY);
    const storage = isLocal ? localStorage : sessionStorage;
    storage.setItem(this.USER_KEY, JSON.stringify(user));
    this.currentUser.set(user);
  }

  /**
   * Cek apakah user yang login memiliki permission tertentu
   */
  hasPermission(permission: string): boolean {
    const user = this.currentUser();
    if (!user || !user.roles) return false;
    for (const r of user.roles) {
      if (typeof r === 'object' && Array.isArray((r as any).permissions)) {
        if ((r as any).permissions.includes(permission)) return true;
      }
    }
    return false;
  }

  /**
   * Cek apakah user memiliki salah satu dari daftar permissions
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((p) => this.hasPermission(p));
  }

  /**
   * Ambil seluruh daftar permission unik milik user
   */
  getUserPermissions(): string[] {
    const user = this.currentUser();
    if (!user || !user.roles) return [];
    const set = new Set<string>();
    for (const r of user.roles) {
      if (typeof r === 'object' && Array.isArray((r as any).permissions)) {
        (r as any).permissions.forEach((p: string) => set.add(p));
      }
    }
    return Array.from(set);
  }

  /**
   * Hapus seluruh sesi login
   */
  clearSession(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.EXPIRES_AT_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.EXPIRES_AT_KEY);
    this.currentUser.set(null);
  }
}
